// JSON-LD Schema generators for SEO
// Uses Schema.org vocabulary for structured data

import { SITE, ORGANIZATION, getFullUrl, getImageUrl } from "./constants";

// Organization schema for the company
export function createOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE.url}/#organization`,
    name: ORGANIZATION.name,
    alternateName: ORGANIZATION.alternateName,
    url: SITE.url,
    logo: getImageUrl(SITE.logo),
    description: SITE.description,
    foundingDate: ORGANIZATION.foundingDate,
    address: {
      "@type": "PostalAddress",
      addressLocality: ORGANIZATION.address.locality,
      addressRegion: ORGANIZATION.address.region,
      postalCode: ORGANIZATION.address.postalCode,
      addressCountry: ORGANIZATION.address.country,
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: ORGANIZATION.email,
      contactType: "customer service",
      availableLanguage: ["English"],
    },
    sameAs: [ORGANIZATION.social.github, ORGANIZATION.social.linkedin],
  };
}

// Software Application schema (for apps like zero)
export function createSoftwareApplicationSchema(product: {
  readonly name: string;
  readonly description: string;
  readonly url: string;
  readonly downloadUrl?: string;
  readonly playStoreUrl?: string;
  readonly appStoreUrl?: string;
  readonly operatingSystem?: string;
  readonly category?: string;
}) {
  const productUrl = getFullUrl(product.url);
  
  // Collect all install URLs for sameAs
  const installUrls: string[] = [];
  if (product.playStoreUrl) installUrls.push(product.playStoreUrl);
  if (product.appStoreUrl) installUrls.push(product.appStoreUrl);
  
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${productUrl}#software`,
    name: product.name,
    description: product.description,
    applicationCategory: product.category || "FinanceApplication",
    operatingSystem: product.operatingSystem || "Android, iOS",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    url: productUrl,
    downloadUrl: product.downloadUrl,
    installUrl: installUrls.length > 0 ? installUrls : undefined,
    sameAs: installUrls.length > 0 ? installUrls : undefined,
    creator: {
      "@type": "Person",
      name: "Indranil Bhuin",
      jobTitle: "Developer",
    },
    publisher: {
      "@id": `${SITE.url}/#organization`,
    },
  };
}

// Book schema (for ebooks)
export function createBookSchema(book: {
  readonly name: string;
  readonly description: string;
  readonly url: string;
  readonly author?: string;
  readonly datePublished?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Book",
    "@id": `${book.url}#book`,
    name: book.name,
    description: book.description,
    url: book.url,
    author: {
      "@type": "Person",
      name: book.author || "Indranil Bhuin",
    },
    publisher: {
      "@id": `${SITE.url}/#organization`,
    },
    datePublished: book.datePublished || "2022",
    bookFormat: "EBook",
    inLanguage: "en",
  };
}

// Breadcrumb schema for navigation hierarchy
export function createBreadcrumbSchema(
  items: ReadonlyArray<{ readonly name: string; readonly url: string }>
) {
  const lastItem = items.at(-1);
  const pageUrl = lastItem ? getFullUrl(lastItem.url) : SITE.url;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: getFullUrl(item.url),
    })),
  };
}

// WebPage schema for generic pages
export function createWebPageSchema(page: {
  readonly name: string;
  readonly description: string;
  readonly url: string;
}) {
  const pageUrl = getFullUrl(page.url);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    name: page.name,
    description: page.description,
    url: pageUrl,
    isPartOf: {
      "@id": `${SITE.url}/#website`,
    },
  };
}

// ContactPage schema
export function createContactPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${SITE.url}/connect#contactpage`,
    name: `Connect with ${SITE.name}`,
    description: `Get in touch with ${ORGANIZATION.name}`,
    url: getFullUrl("/connect"),
    mainEntity: {
      "@id": `${SITE.url}/#organization`,
    },
    isPartOf: {
      "@id": `${SITE.url}/#website`,
    },
  };
}

// WebSite schema for homepage (helps with sitelinks)
export function createWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE.url}/#website`,
    name: SITE.name,
    alternateName: ORGANIZATION.name,
    url: SITE.url,
    description: SITE.description,
    publisher: {
      "@id": `${SITE.url}/#organization`,
    },
    inLanguage: SITE.locale,
  };
}
